import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";

type DownloadReportResponse = {
  government_id_url: null;
  include_government_id_report: null | string;
  include_redacted_report: null | string;
  redacted_report_url: null | string;
  status: "processing" | "finished";
  verification_name: null;
  verification_report_url: null | string;
};

type DownloadReportConfig = {
  include_government_id_report: boolean;
  include_redacted_report: boolean;
};

const programmaticDownload = (url: string, newPage?: boolean) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = url.split("/").pop() || "file";
  document.body.appendChild(a);

  if (newPage) a.target = "_blank";

  a.click();
  document.body.removeChild(a);
};

const getQueryKey = ({
  applicationId,
  applicantId,
  reportId
}: {
  applicationId: string;
  applicantId: string;
  reportId?: string; // Will be replaced with "ALL"
}) => [
  "POLLING",
  "PRINT_REPORT",
  reportId || "ALL",
  "APPLICATION",
  applicationId,
  "APPLICANT",
  applicantId
];

/**
 * Hook to download report PDF
 * @param applicationId - The ID of the application
 * @param applicantId - The ID of the applicant
 * @param reportId - The ID of the report (optional)
 * @param config - Configuration object for the request
 * @returns { startPolling: () => void, isPolling: boolean }
 */
export const useDownloadReportPdf = ({
  applicationId,
  applicantId,
  reportId
}: {
  applicationId: string;
  applicantId: string;
  reportId?: string; // Will be replaced with "ALL"
}) => {
  const [isPolling, setIsPolling] = useState(false);

  const [config, setConfig] = useState<DownloadReportConfig>();

  const queryKey = useMemo(
    () => getQueryKey({ applicationId, applicantId, reportId }),
    [applicationId, applicantId, reportId, config]
  );

  const { refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      try {
        const response = await instance.post<DownloadReportResponse>(
          `/portal/v1/applications/${applicationId}/applicants/${applicantId}/reports`,
          {
            ...config,
            verification_name: reportId
          }
        );

        if (response.data.status === "finished") {
          setIsPolling(false);

          if (config?.include_redacted_report) {
            if (!response.data.redacted_report_url)
              throw new Error("Redacted report URL is not available");

            programmaticDownload(response.data.redacted_report_url, true);
          } else {
            if (!response.data.verification_report_url)
              throw new Error("Redacted ID report URL is not available");

            programmaticDownload(response.data.verification_report_url, true);
          }
        }

        return response.data;
      } catch (error) {
        setIsPolling(false);
        toast.error(
          getErrorMessage(
            error,
            "An error occurred while generating the report"
          )
        );
        throw error;
      }
    },

    refetchInterval: 5000,
    enabled: isPolling
  });

  const startPolling = (config: DownloadReportConfig) => {
    setConfig(config);
    setIsPolling(true);
    refetch();
  };

  return {
    startPolling,
    isPolling
  };
};
